import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { setSentryUser, clearSentryUser } from '../lib/sentry';
import { validatePassword } from '../lib/utils/validators';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  user_type: 'patient' | 'psychologist' | 'admin';
  phone: string | null;
  cpf: string | null;
  photo_url: string | null;
  onboarding_completed?: boolean;
  must_reset_password?: boolean;
  onboarding_step?: number;
  created_at?: string;
  psychologist_profile_id?: string;
  plan_id?: string;
  plan_type?: string;
  session_price?: number;
  crp?: string;
}

interface AuthContextValue {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  /**
   * MFA: sessão em AAL1 mas o usuário tem fator TOTP verificado → precisa
   * passar pelo desafio em /verificar-2fa antes de qualquer rota protegida.
   */
  mfaPending: boolean;
  refreshMfaStatus: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    metadata: {
      full_name: string;
      user_type: 'patient' | 'psychologist';
      phone?: string;
      cpf?: string;
    },
    captchaToken?: string
  ) => Promise<{ success: boolean; error?: string }>;
  signIn: (
    email: string,
    password: string,
    captchaToken?: string
  ) => Promise<{ success: boolean; error?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  signInWithApple: () => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string; message?: string }>;
  refreshProfile: () => void;
  isAuthenticated: boolean;
  userRole: string | undefined;
  isPsychologist: boolean;
  isPatient: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function keepSameUser(prev: User | null, next: User | null): User | null {
  if (prev && next && prev.id === next.id && prev.updated_at === next.updated_at) return prev;
  return next;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);
  const loadingForUserIdRef = useRef<string | null>(null);
  // Usuário cujo profile já está carregado. O supabase-js reemite SIGNED_IN
  // toda vez que a aba volta a ficar visível com a sessão válida
  // (_recoverAndRefresh) — sem isto o app zerava o profile, o ProtectedRoute
  // trocava a tela pelo "Carregando..." e a página desmontava, perdendo o que
  // estava em memória (relatório, editor de prontuário).
  const profileLoadedForRef = useRef<string | null>(null);

  // Lê o claim `aal` do JWT local e os fatores do usuário (sem rede). Se há
  // fator TOTP verificado e a sessão ainda é AAL1, o desafio é obrigatório.
  const computeMfaPending = async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error || !data) return false;
      return data.currentLevel === 'aal1' && data.nextLevel === 'aal2';
    } catch {
      return false;
    }
  };

  const refreshMfaStatus = async () => {
    setMfaPending(await computeMfaPending());
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadingForUserIdRef.current = session.user.id;
        loadProfile(session.user.id);
      } else {
        loadingForUserIdRef.current = null;
        setProfile(null);
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return;
      // Mesmo usuário: mantém a MESMA referência de user. Um objeto novo a cada
      // renovação de token ou volta de aba re-disparava todo efeito com [user]
      // nas deps (a página do paciente refazia as consultas). O token não mora
      // no objeto user — o client do supabase usa o da sessão.
      if (event === 'TOKEN_REFRESHED') {
        setUser((prev) => keepSameUser(prev, session?.user ?? null));
        void refreshMfaStatus();
        return;
      }

      if (event === 'SIGNED_IN' && session?.user && profileLoadedForRef.current === session.user.id) {
        setUser((prev) => keepSameUser(prev, session.user));
        void refreshMfaStatus();
        return;
      }

      setUser(session?.user ?? null);
      if (session?.user) {
        loadingForUserIdRef.current = session.user.id;
        setProfile(null);
        setLoading(true);
        loadProfile(session.user.id);
      } else {
        loadingForUserIdRef.current = null;
        profileLoadedForRef.current = null;
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
    // Assinatura única no mount — loadProfile/refreshMfaStatus são estáveis por design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async (userId: string) => {
    loadingForUserIdRef.current = userId;
    try {
      // MFA resolvido ANTES de loading=false — o ProtectedRoute precisa saber se
      // exige o desafio já no primeiro roteamento, sem flash do dashboard.
      const [{ data, error }, pending] = await Promise.all([
        supabase.from('users').select('*').eq('id', userId).maybeSingle(),
        computeMfaPending(),
      ]);
      if (loadingForUserIdRef.current === userId) setMfaPending(pending);

      if (error) throw error;

      if (data && data.user_type === 'psychologist') {
        const { data: psychProfile } = await supabase
          .from('psychologist_profiles')
          .select('id, crp, plan_id, plan_type, session_price')
          .eq('user_id', userId)
          .maybeSingle();

        data.psychologist_profile_id = psychProfile?.id ?? undefined;
        data.plan_id = psychProfile?.plan_id ?? undefined;
        data.plan_type = psychProfile?.plan_type ?? undefined;
        data.session_price = psychProfile?.session_price != null
          ? Number(psychProfile.session_price)
          : undefined;
        data.crp = psychProfile?.crp ?? undefined;
      }

      if (loadingForUserIdRef.current === userId) {
        setProfile(data ?? null);
        profileLoadedForRef.current = data ? userId : null;
        if (data) setSentryUser(userId, data.user_type);
      }
    } catch (_error: unknown) {
      if (loadingForUserIdRef.current === userId) {
        setProfile(null);
        profileLoadedForRef.current = null;
      }
    } finally {
      if (loadingForUserIdRef.current === userId) {
        setLoading(false);
      }
    }
  };

  const signUp = async (
    email: string,
    password: string,
    metadata: {
      full_name: string;
      user_type: 'patient' | 'psychologist';
      phone?: string;
      cpf?: string;
    },
    captchaToken?: string
  ) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          captchaToken,
          data: {
            full_name: metadata.full_name,
            user_type: metadata.user_type,
          },
        },
      });

      if (authError) {
        console.error('signUp error:', authError.message);
        return { success: false, error: 'Não foi possível criar a conta. Verifique os dados e tente novamente.' };
      }
      if (!authData.user) return { success: false, error: 'Não foi possível criar a conta. Tente novamente.' };

      if (metadata.phone) {
        const { error: phoneError } = await supabase
          .from('users')
          .update({ phone: metadata.phone })
          .eq('id', authData.user.id);
        if (phoneError) {
          console.warn('signUp: falha ao salvar phone (não-bloqueante):', phoneError.message);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Erro no cadastro:', error);
      return { success: false, error: 'Não foi possível criar a conta. Tente novamente.' };
    }
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });
      if (error) throw error;

      if (data.user && !validatePassword(password)) {
        await supabase
          .from('users')
          .update({ must_reset_password: true })
          .eq('id', data.user.id);
        await loadProfile(data.user.id);
      }

      return { success: true };
    } catch (error: any) {
      console.error('signIn error:', error?.message);
      return { success: false, error: 'E-mail ou senha incorretos. Tente novamente.' };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const redirectUrl = `${window.location.origin}/login`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Google signIn error:', error?.message);
      return { success: false, error: 'Não foi possível conectar com o Google. Tente novamente.' };
    }
  };

  const signInWithApple = async () => {
    try {
      const base = window.location.origin + (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '/');
      const redirectUrl = base.endsWith('/') ? base + 'login' : base + '/login';
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: { redirectTo: redirectUrl },
      });
      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Apple signIn error:', error?.message);
      return { success: false, error: 'Não foi possível conectar com a Apple. Tente novamente.' };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      clearSentryUser();
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });

      if (error) {
        console.error('Erro ao enviar e-mail de recuperação:', error);
        return { success: false, error: 'Não foi possível enviar o e-mail. Tente novamente em alguns minutos.' };
      }

      return {
        success: true,
        message: 'Se houver uma conta com este e-mail, você receberá um link de recuperação em alguns minutos.',
      };
    } catch (error: unknown) {
      console.error('Erro ao gerar link de recuperação:', error);
      return { success: false, error: 'Ocorreu um erro inesperado. Tente novamente.' };
    }
  };

  const refreshProfile = () => {
    if (user) loadProfile(user.id);
  };

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    mfaPending,
    refreshMfaStatus,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    resetPassword,
    refreshProfile,
    isAuthenticated: !!user,
    userRole: profile?.user_type,
    isPsychologist: profile?.user_type === 'psychologist',
    isPatient: profile?.user_type === 'patient',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
